import React from 'react';
import { DatalayerItem, TrackingService } from '@rocheglobal/component-library-dot-com/src/services/tracking';
import { HistoryService } from '@rocheglobal/component-library-dot-com/src/services/history';
import { Link, NavigationLink, NavigationProps } from '../../services/navigation/types';
import { NavigationService } from '../../services/navigation';
import { AlternateLanguageLink } from '../../services/dom/types';
import { AdobeTracking } from '../../tracking-services';
import { DefaultLinkClick } from '../../tracking-services/types';

const currentPageClass = (link: Link): string =>
  NavigationService.isCurrentPage(link) ? NavigationService.currentPageCssClass : '';

const defaultTracking: DatalayerItem = {
  event: 'navigation_click',
  component_name: 'roche-navigation',
};

const defaultAdobeTrackingData = {
  position: 'Navigation',
  type: 'Link',
  socialMedia: '',
  socialInteraction: '',
};

const trackLinkClick = (event): void => {
  const targetLink = event.target?.getAttribute('href')
    ? event.target?.getAttribute('href')
    : event.target?.parentElement?.getAttribute('href');

  const targetText = event.target?.innerText ? event.target?.innerText : event.target?.parentElement?.innerText;

  TrackingService.push({
    ...defaultTracking,
    additionalParameters: {
      current_location: HistoryService.getActiveUrl().pathname,
      link_text: targetText,
    },
    value: targetLink,
  });

  AdobeTracking(
    {
      ...defaultAdobeTrackingData,
      url: targetLink,
      innerText: targetText,
    } as DefaultLinkClick,
    'defaultLinkClick',
  );
};

const handlePointerDown = (event) => {
  const { target, pointerType } = event;
  target.setAttribute('clicktype', pointerType);
};

const handleClick = (event) => {
  if (event.target.getAttribute('clicktype') === 'touch') {
    event.preventDefault();
    return;
  }
  trackLinkClick(event);
};

const renderNavigationItem = (link: NavigationLink, index): JSX.Element => {
  const { nested_links: nested, text, url, addRightArrow } = link;

  // separator
  if (!url) {
    return <li key={index}></li>;
  }

  // Top level
  if (nested?.length > 0) {
    return (
      <li key={index} className={currentPageClass(link.url)}>
        <span>
          <a
            href={NavigationService.getParsedNavigationLink(url)}
            onClick={handleClick}
            onPointerDown={handlePointerDown}
          >
            {text}
          </a>
        </span>
        <ul>{[{ text, url, addRightArrow: true }, ...nested].map(renderNavigationItem)}</ul>
      </li>
    );
  }

  return (
    <li key={index} className={currentPageClass(link.url)}>
      <a href={NavigationService.getParsedNavigationLink(url)} onClick={trackLinkClick}>
        {text}
        {addRightArrow && <roche-icon icon='arrow-right' height='24px'></roche-icon>}
      </a>
    </li>
  );
};

const renderSeparator = (alternates: AlternateLanguageLink[], worldWideLink: Link) =>
  alternates.length > 0 || worldWideLink !== undefined ? <li /> : null;

const renderWorldWideLink = (link: Link) =>
  NavigationService.getParsedLink(link) !== '' ? (
    <>
      <li className={currentPageClass(link)}>
        <a href={NavigationService.getParsedNavigationLink(link)} onClick={trackLinkClick}>
          <roche-icon icon='globe' height='20px'></roche-icon>
        </a>
      </li>
    </>
  ) : null;

const renderGlobalSearchLink = (link: Link) =>
  NavigationService.getParsedLink(link) !== '' ? (
    <li className={currentPageClass(link)}>
      <a href={NavigationService.getParsedNavigationLink(link)} onClick={trackLinkClick}>
        <roche-icon icon='search' height='20px'></roche-icon>
      </a>
    </li>
  ) : null;

export const Navigation = ({
  slot,
  tree = [],
  worldWideLink,
  globalSearchLink,
  alternates = [],
}: NavigationProps): JSX.Element => (
  <roche-navigation slot={slot}>
    <ul>
      {(tree as NavigationLink[]).map(renderNavigationItem)}
      {renderSeparator(alternates, worldWideLink)}
      {/* This will display an error until the component library and its types are updated */}
      {alternates.length > 0 && (
        <li className='reduced-margin'>
          {/* @ts-ignore next-line */}
          <roche-language-switch alternates={JSON.stringify(alternates)} layout='dropdown' />
        </li>
      )}
      {worldWideLink && renderWorldWideLink(worldWideLink)}
      {globalSearchLink && renderGlobalSearchLink(globalSearchLink)}
    </ul>
  </roche-navigation>
);
